import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  AppUserAttachment,
  AppUserListResponse,
  AppUserProfile,
  AppUserService,
  AttachmentService,
  TokenStorageService
} from '../../core/services';
import { EntityListComponent, EntityListMenuAction, TableHeaderAndValueKeySets } from '../../core/ui/entity-list/entity-list.component';
import {
  appendUniqueById,
  createListPaginationState,
  hasMoreByTotal,
  resolveRequestPage,
  resetListPaginationState,
  updateAfterFetch
} from '../../core/utils/list-pagination.util';
import { AddEditEmployeeComponent, EmployeeSaveEvent } from './add-edit-employee/add-edit-employee.component';
import {
  ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE,
  ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE,
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  FeatureTypeEnum
} from '../../core/constants/app-constants';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { SubscriptionService, SubscriptionUsageSummary } from '../../core/services/subscription.service';
import { TranslatePipe } from '../../core/pipes/translate.pipe';
import { LanguageService } from '../../core/services/language.service';
import { ImageCropModalComponent } from '../../core/ui/image-crop-modal/image-crop-modal.component';

interface EmployeeRow extends AppUserProfile {
  nameTopLine: string;
  nameBottomLine: string;
}

@Component({
  selector: 'app-employee-list',
  standalone: true,
  imports: [CommonModule, MatSnackBarModule, EntityListComponent, AddEditEmployeeComponent, ConfirmActionModalComponent, TranslatePipe, ImageCropModalComponent],
  templateUrl: './employee-list.component.html',
  styleUrls: ['./employee-list.component.css', './../../../CSSFiles/BTN_Style.css']
})
export class EmployeeListComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isLoading = false;
  isLoadingMore = false;
  isDeleting = false;
  employees: EmployeeRow[] = [];
  subscriptionSummary: SubscriptionUsageSummary | null = null;
  error: string | null = null;
  status: string | null = null;
  filters: Record<string, any> = {};
  private readonly paginationState = createListPaginationState();
  private accountScopeId: number | null = null;
  private summaryRefreshSubscription?: Subscription;

  isEditOpen = false;
  isDetailsOpen = false;
  selectedEmployee: AppUserProfile | null = null;
  pendingDeleteEmployee: AppUserProfile | null = null;
  isUploadModalOpen = false;
  isUploadingAttachment = false;
  uploadFeatureId: number | null = null;
  uploadFiles: File[] = [];
  uploadExistingAttachments: AppUserAttachment[] = [];
  deletingAttachmentIds: number[] = [];
  uploadMode: 'create' | 'edit' = 'create';
  uploadError: string | null = null;
  pendingDeleteOnlyUpload = false;
  uploadCropSourceFile: File | null = null;
  imagePreviewUrl: string | null = null;
  imagePreviewName: string | null = null;
  isImagePreviewOpen = false;
  employeeProfileImageUrl: string | null = null;
  isEmployeeUsernameVisible = false;
  employeeUsernameCopied = false;

  readonly employeeImageFeatureType = FeatureTypeEnum.ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE;
  readonly employeeImageMaxFileCount = ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE[FeatureTypeEnum.ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE];
  readonly employeeImageAllowedExtensionsText = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[FeatureTypeEnum.ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE].join(', ');
  readonly employeeMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true, requiredService: 'EMPLOYEE_DELETE' }
  ];

  tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    'table.name': {
      topLineKey: 'nameTopLine',
      bottomLineKyes: ['nameBottomLine']
    },
    'table.mobile': 'mobileNumber',
    'table.email': 'email'
  };

  get hasMoreEmployees(): boolean {
    return hasMoreByTotal(this.employees, this.paginationState);
  }

  get isCreateDisabled(): boolean {
    return this.activeEmployeeCount >= this.employeeCreateLimit;
  }

  get deletedMode(): boolean {
    return Number(this.filters?.['status']) === 0;
  }

  get activeEmployeeCount(): number {
    return Number(this.subscriptionSummary?.usage?.activeEmployeeCount ?? 0);
  }

  get employeeCreateLimit(): number {
    return Number(this.subscriptionSummary?.currentPackage?.maxActiveEmployee ?? 0);
  }

  constructor(
    private readonly appUsers: AppUserService,
    private readonly attachments: AttachmentService,
    private readonly tokens: TokenStorageService,
    private readonly subscriptionService: SubscriptionService,
    private readonly snackBar: MatSnackBar,
    private readonly language: LanguageService
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.tokens.hasActiveSession;
    if (!this.isAuthenticated) {
      this.error = 'Please log in first.';
      return;
    }

    this.status = 'Loading subuser list...';
    this.loadSubscriptionSummary();
    this.summaryRefreshSubscription = this.subscriptionService.summaryRefresh$.subscribe(() => {
      this.loadSubscriptionSummary();
    });
    this.resolveAccountScopeAndLoad();
  }

  ngOnDestroy(): void {
    this.summaryRefreshSubscription?.unsubscribe();
    this.uploadCropSourceFile = null;
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...(filters ?? {}) };
    this.fetchEmployees(true);
  }

  fetchEmployees(reset = true): void {
    if (!Number.isFinite(this.accountScopeId)) {
      return;
    }
    if ((reset && this.isLoading) || (!reset && (this.isLoading || this.isLoadingMore || !this.hasMoreEmployees))) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.error = null;
      this.employees = [];
      resetListPaginationState(this.paginationState);
    } else {
      this.isLoadingMore = true;
    }

    const requestPage = resolveRequestPage(this.paginationState, reset);
    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }

    this.appUsers.listEmployees({
      pagination: this.paginationState.pagination,
      pageNo: requestPage,
      rowCount: this.paginationState.rowCount,
      filters: {
        ...filters,
        accountId: this.accountScopeId
      }
    }).pipe(
      finalize(() => {
        if (reset) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
      })
    ).subscribe({
      next: (res: AppUserListResponse) => {
        if (res?.code !== '000') {
          this.error = res?.message || 'Failed to fetch subuser list.';
          return;
        }

        const nextRows = (res.data || []).map((employee) => ({
          ...employee,
          nameTopLine: employee.name || 'N/A',
          nameBottomLine: employee.username || employee.email || 'N/A'
        }));

        updateAfterFetch(this.paginationState, res?.totalRows, requestPage, nextRows.length);
        this.employees = reset ? nextRows : appendUniqueById(this.employees, nextRows);

        const selectedEmployeeId = Number(this.selectedEmployee?.id);
        if (Number.isFinite(selectedEmployeeId)) {
          const latestSelectedEmployee = this.employees.find((item) => Number(item.id) === selectedEmployeeId);
          if (latestSelectedEmployee) {
            this.selectedEmployee = latestSelectedEmployee;
          }
        }

        this.status = `Total subusers: ${this.activeEmployeeCount}`;
      },
      error: (err: any) => {
        this.error = err?.message || 'Failed to fetch subuser list.';
      }
    });
  }

  onLoadMoreFromList(): void {
    this.fetchEmployees(false);
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedEmployee = item as AppUserProfile;
    this.isDetailsOpen = true;
    this.loadEmployeeProfileImage();
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.selectedEmployee = item as AppUserProfile;
    this.isDetailsOpen = false;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    if (this.isDeleting) {
      return;
    }

    this.pendingDeleteEmployee = item as AppUserProfile;
  }

  closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteEmployee = null;
  }

  confirmDeleteEmployee(): void {
    const employee = this.pendingDeleteEmployee;
    if (!employee || this.isDeleting) {
      return;
    }

    const employeeId = Number(employee?.id);
    if (!Number.isFinite(employeeId)) {
      this.error = 'Invalid subuser id.';
      this.pendingDeleteEmployee = null;
      return;
    }

    this.isDeleting = true;
    this.error = null;
    this.appUsers.deleteEmployee(employeeId)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: (res: any) => {
          if (`${res?.code ?? ''}` !== '000') {
            this.pendingDeleteEmployee = null;
            this.error = `${res?.message ?? 'Failed to delete subuser.'}`;
            return;
          }
          this.pendingDeleteEmployee = null;
          this.fetchEmployees(true);
        },
        error: (err: any) => {
          this.pendingDeleteEmployee = null;
          this.error = err?.message || 'Failed to delete subuser.';
        }
      });
  }

  openUploadAfterCreate(refId: number): void {
    if (!Number.isFinite(Number(refId))) {
      return;
    }

    this.uploadFeatureId = Number(refId);
    this.uploadMode = 'create';
    this.uploadFiles = [];
    this.deletingAttachmentIds = [];
    this.uploadExistingAttachments = [];
    this.uploadError = null;
    this.isUploadModalOpen = true;
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.selectedEmployee = null;
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
    this.employeeProfileImageUrl = null;
    this.isEmployeeUsernameVisible = false;
    this.employeeUsernameCopied = false;
    this.selectedEmployee = null;
  }

  onEditSave(_: EmployeeSaveEvent): void {
    this.closeEditModal();
    this.fetchEmployees(true);
  }

  openEditImageModal(): void {
    if (!this.selectedEmployee?.id) {
      return;
    }
    this.uploadFeatureId = Number(this.selectedEmployee.id);
    this.uploadMode = 'edit';
    this.uploadFiles = [];
    this.deletingAttachmentIds = [];
    this.uploadExistingAttachments = Array.isArray(this.selectedEmployee.attachments)
      ? [...this.selectedEmployee.attachments]
      : [];
    this.uploadError = null;
    this.uploadCropSourceFile = null;
    this.isUploadModalOpen = true;
  }

  createLimitMessage(): string {
    return `Subuser create limit is ${this.employeeCreateLimit}.`;
  }

  private loadSubscriptionSummary(): void {
    this.subscriptionService.getSubscriptionSummary().subscribe({
      next: (summary) => {
        this.subscriptionSummary = summary;
      },
      error: () => {
        this.subscriptionSummary = null;
      }
    });
  }

  onUploadFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const incoming = Array.from(input?.files ?? []);

    if (this.employeeImageMaxFileCount === 1) {
      if (this.uploadExistingAttachments.length >= 1) {
        this.uploadError = 'Remove the existing subuser image before selecting a new one.';
        if (input) {
          input.value = '';
        }
        return;
      }

      const selected = incoming[0];
      if (!selected) {
        this.uploadError = null;
        if (input) {
          input.value = '';
        }
        return;
      }

      const selectedName = `${selected.name ?? ''}`.toLowerCase();
      const allowedExtensions = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[this.employeeImageFeatureType];
      if (!allowedExtensions.some((ext) => selectedName.endsWith(ext))) {
        this.uploadError = `Only ${this.employeeImageAllowedExtensionsText} files are allowed.`;
        if (input) {
          input.value = '';
        }
        return;
      }

      this.uploadError = incoming.length > 1 ? 'Only 1 file allowed for subuser image.' : null;
      this.uploadCropSourceFile = selected;

      if (input) {
        input.value = '';
      }
      return;
    }
  }

  uploadEmployeeImage(): void {
    if (!this.uploadFeatureId) {
      this.uploadError = 'Invalid subuser id for attachment upload.';
      return;
    }

    const willDeleteOnly = this.uploadMode === 'edit' && this.deletingAttachmentIds.length > 0 && this.uploadFiles.length === 0;
    if (willDeleteOnly) {
      this.pendingDeleteOnlyUpload = true;
      return;
    }
    this.performEmployeeImageUpload();
  }

  confirmDeleteOnlyUpload(): void {
    if (!this.pendingDeleteOnlyUpload) {
      return;
    }
    this.pendingDeleteOnlyUpload = false;
    this.performEmployeeImageUpload();
  }

  closeDeleteOnlyUploadModal(): void {
    this.pendingDeleteOnlyUpload = false;
  }

  private performEmployeeImageUpload(): void {
    const featureId = this.uploadFeatureId;
    if (!featureId) {
      this.uploadError = 'Invalid subuser id for attachment upload.';
      return;
    }

    const willDeleteOnly = this.uploadMode === 'edit' && this.deletingAttachmentIds.length > 0 && this.uploadFiles.length === 0;
    if (!this.uploadFiles.length && !willDeleteOnly) {
      this.uploadError = 'Please select at least one file.';
      return;
    }

    const finalCount = this.uploadExistingAttachments.length + this.uploadFiles.length;
    if (finalCount > this.employeeImageMaxFileCount) {
      this.uploadError = `Maximum ${this.employeeImageMaxFileCount} file allowed for subuser image.`;
      return;
    }

    const invalid = this.uploadFiles.find((file) => {
      const name = `${file.name ?? ''}`.toLowerCase();
      return !ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[this.employeeImageFeatureType].some((ext) => name.endsWith(ext));
    });
    if (invalid) {
      this.uploadError = `Only ${this.employeeImageAllowedExtensionsText} files are allowed.`;
      return;
    }

    const oversize = this.uploadFiles.find((file) => Number(file?.size ?? 0) > ATTACHMENT_MAX_FILE_SIZE_BYTES);
    if (oversize) {
      this.uploadError = 'Each file must be 2MB or smaller.';
      return;
    }

    this.isUploadingAttachment = true;
    this.uploadError = null;

    this.attachments.manageAttachment({
      featureType: this.employeeImageFeatureType,
      featureId,
      actionType: this.uploadMode === 'edit' ? 2 : 1,
      files: this.uploadFiles,
      deletingIds: this.deletingAttachmentIds,
      service: 'EMPLOYEE_IMAGE_CREATE'
    })
      .pipe(finalize(() => (this.isUploadingAttachment = false)))
      .subscribe({
        next: () => {
          if (this.uploadMode === 'edit') {
            this.snackBar.open(
              this.language.translate('user.imageUpdatedSuccess', 'Subuser image updated successfully.'),
              this.language.translate('common.close', 'Close'),
              {
                duration: 3000,
                horizontalPosition: 'right',
                verticalPosition: 'top'
              }
            );
          }
          this.attachments.clearPreviewUrlCache();
          this.closeUploadModal();
          this.fetchEmployees(true);
        },
        error: (err: any) => {
          this.uploadError = err?.message || 'Failed to upload attachment.';
        }
      });
  }

  closeUploadModal(): void {
    this.isUploadModalOpen = false;
    this.uploadFeatureId = null;
    this.uploadFiles = [];
    this.uploadExistingAttachments = [];
    this.deletingAttachmentIds = [];
    this.uploadMode = 'create';
    this.uploadError = null;
    this.pendingDeleteOnlyUpload = false;
    this.uploadCropSourceFile = null;
  }

  skipUploadModal(): void {
    this.closeUploadModal();
  }

  removeUploadFile(index: number): void {
    if (index < 0 || index >= this.uploadFiles.length) {
      return;
    }
    this.uploadFiles = this.uploadFiles.filter((_, i) => i !== index);
  }

  onEmployeeImageCropCancelled(): void {
    this.uploadCropSourceFile = null;
  }

  onEmployeeImageCropped(file: File): void {
    this.uploadCropSourceFile = null;
    this.uploadFiles = [file];
    this.uploadError = null;
  }

  removeExistingAttachment(index: number): void {
    const target = this.uploadExistingAttachments[index];
    if (!target) {
      return;
    }
    const attachmentId = Number(target.id);
    if (Number.isFinite(attachmentId)) {
      this.deletingAttachmentIds = appendUniqueById(
        this.deletingAttachmentIds.map((id) => ({ id })),
        [{ id: attachmentId }]
      ).map((item) => Number(item.id));
    }
    this.uploadExistingAttachments = this.uploadExistingAttachments.filter((_, i) => i !== index);
  }

  openAttachmentPreview(attachment: AppUserAttachment): void {
    const attachmentId = Number(attachment?.id);
    if (!Number.isFinite(attachmentId)) {
      return;
    }

    this.attachments.previewUrlByAttachmentId(attachmentId).subscribe({
      next: (url: string) => {
        this.imagePreviewUrl = `${url ?? ''}`.trim() || null;
        this.imagePreviewName = `${attachment?.attachmentFileName ?? ''}`.trim() || 'Attachment';
        this.isImagePreviewOpen = !!this.imagePreviewUrl;
      },
      error: (err: any) => {
        this.uploadError = err?.message || 'Failed to open attachment preview.';
      }
    });
  }

  closeImagePreview(): void {
    this.isImagePreviewOpen = false;
    this.imagePreviewUrl = null;
    this.imagePreviewName = null;
  }

  firstEmployeeImageAttachment(): AppUserAttachment | null {
    const attachments = Array.isArray(this.selectedEmployee?.attachments) ? this.selectedEmployee.attachments : [];
    const match = attachments.find((item) => this.isImageFile(item?.attachmentFileName));
    return match || null;
  }

  employeeInitials(): string {
    const name = `${this.selectedEmployee?.name ?? ''}`.trim();
    if (!name) {
      return 'E';
    }

    const parts = name.split(/\s+/).filter((part) => !!part);
    if (parts.length === 1) {
      return parts[0].slice(0, 1).toUpperCase();
    }

    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
  }

  employeeUsernameValue(): string {
    const username = `${this.selectedEmployee?.username ?? ''}`.trim();
    return username || 'N/A';
  }

  maskedEmployeeUsername(): string {
    const username = this.employeeUsernameValue();
    if (!username || username === 'N/A') {
      return 'N/A';
    }
    return '•'.repeat(Math.max(8, username.length));
  }

  toggleEmployeeUsernameVisibility(): void {
    if (this.employeeUsernameValue() === 'N/A') {
      return;
    }
    this.isEmployeeUsernameVisible = !this.isEmployeeUsernameVisible;
  }

  async copyEmployeeUsername(): Promise<void> {
    const username = this.employeeUsernameValue();
    if (username === 'N/A') {
      return;
    }

    const copied = await this.copyText(username);
    this.employeeUsernameCopied = copied;
    if (copied) {
      window.setTimeout(() => {
        this.employeeUsernameCopied = false;
      }, 1500);
    }
  }

  private resolveAccountScopeAndLoad(): void {
    this.appUsers.me().subscribe({
      next: (profile) => {
        this.accountScopeId = this.resolveAccountScopeId(profile);
        this.fetchEmployees(true);
      },
      error: () => {
        this.appUsers.basicInfoFromList().subscribe({
          next: (profile) => {
            this.accountScopeId = this.resolveAccountScopeId(profile);
            this.fetchEmployees(true);
          },
          error: () => {
            this.error = 'Failed to resolve account information.';
          }
        });
      }
    });
  }

  private resolveAccountScopeId(profile: AppUserProfile | null): number | null {
    const accountId = Number(profile?.accountId);
    if (Number.isFinite(accountId) && accountId > 0) {
      return accountId;
    }
    const profileId = Number(profile?.id);
    return Number.isFinite(profileId) ? profileId : null;
  }

  private loadEmployeeProfileImage(): void {
    this.employeeProfileImageUrl = null;
    const attachment = this.firstEmployeeImageAttachment();
    const attachmentId = Number(attachment?.id);
    if (!Number.isFinite(attachmentId)) {
      return;
    }

    this.attachments.previewUrlByAttachmentId(attachmentId).subscribe({
      next: (url: string) => {
        this.employeeProfileImageUrl = `${url ?? ''}`.trim() || null;
      },
      error: () => {
        this.employeeProfileImageUrl = null;
      }
    });
  }

  private isImageFile(fileName: string | undefined): boolean {
    const normalized = `${fileName ?? ''}`.trim().toLowerCase();
    return normalized.endsWith('.jpg') || normalized.endsWith('.jpeg') || normalized.endsWith('.png');
  }

  private async copyText(value: string): Promise<boolean> {
    const text = `${value ?? ''}`.trim();
    if (!text) {
      return false;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fallback below for browsers that block async clipboard access.
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
